<template>
  <div class='centralized-issues-data-view'>
    <div class='issues-list-header'>
      <div class='title'>
        {{ $t('centralized.issues.view.title') }}
      </div>
      <div class='actions'>
        <div class='priority-sort' @click='sortByPriority'>
          <v-icon>sort</v-icon>
          <span> {{ $t('show.prioritized.issues.view.label') }} </span>
          <v-icon small>{{ iconForPrioritySorting }}</v-icon>
        </div>
        <download-c-s-v class="download-csv-btn" @click="downloadCSVReport" :class="{'disabled': issuesList.length === 0}"/>
      </div>
    </div>
    <div class='counter'> {{getCounter}} </div>
    <div class='bulk-actions-bar'>
      <v-simple-checkbox
        class='bulk-actions-bar__select-all'
        :value='selectedCount > 0'
        :indeterminate='isIndeterminate'
        :ripple='false'
        color='primary'
        dense
        @input='selectAll'
      />
      <span class='bulk-actions-bar__label' v-if='hasSelection'>
        {{ selectedCount }} {{ $t('centralized.issues.bulk.selected') }}
      </span>
      <v-spacer />
      <v-menu offset-y v-if='hasSelection' content-class='bulk-actions-menu'>
        <template #activator='{ on, attrs }'>
          <v-btn
            class='bulk-actions-bar__actions-btn'
            :loading='suppressionActionLoading'
            v-bind='attrs'
            v-on='on'
          >
            {{ $t('centralized.issues.bulk.actions') }}
            <v-icon small right>arrow_drop_down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click='openSuppressDialog(null, null)'>
            <v-list-item-title>{{ $t('centralized.issues.bulk.suppress') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click='openRestoreConfirm(null)'>
            <v-list-item-title>{{ $t('centralized.issues.bulk.unsuppress') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <div class='issues-list-content' :class='{ loading: loading }'>
      <wombat-table
        class="issues-table"
        :items="issuesList"
        :columns="tableColumns"
        :resize="false"
        :loadingItems="false"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
        @columnSorted="columnSorted"
      >
        <component
          slot="row-cell"
          slot-scope="rowCell"
          class="row-cell"
          :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
          :item="rowCell.item"
          :column="rowCell.column"
          :selected-ids="effectiveSelectedIdsOnPage"
          @toggleSelect='toggleIssueSelection'
          @openDetailedIssuePopup='toggleDetailedIssuePopup'
          @integrationClicked='integrationClicked'
        />
      </wombat-table>
      <div class="issues-list-content__footer">
        <advanced-pagination
          tab='centralizedIssues'
          :pages-amount='pages'
          :is-show-pagination='isShowPagination'
          @changedSelectedPage='changedSelectedPage'
          @changedItemsAmountPerPage='changedItemsAmountPerPage'
        />
      </div>
    </div>
    <div class='issues-tab-loading' v-if='loading' />
    <page-loading-overlay
      :show="loading"
      :progress="issuesProgress"
      contained
    />
    <automatic-issue-details-popup
      :forCentralizedView='true'
      :visible-popup='openDetailedIssuePopup'
      :issue='detailedIssueForCentralizedView'
      :refresh-key='detailsRefreshKey'
      @closed='openDetailedIssuePopup = false'
      @suppress='openSuppressDialog'
      @restore='openRestoreConfirm'
      @suppressionLoaded='onSuppressionLoaded'
    >
      <component
        slot="issue-body"
        slot-scope="issueBody"
        class="issue-body"
        :is="issueBodyComponentsHash[issueBody.issue.name] || issueBodyComponentsHash.default"
        :issue="issueBody.issue"
      />
    </automatic-issue-details-popup>
    <ticketing-integration-popup
      :internalValue='openTicketingIntegrationPopup'
      title='technologies.jira.integration.popup.title'
      message='technologies.jira.integration.popup.message'
      ok-caption='technologies.jira.integration.popup.ok'
      @clicked='openTicketingIntegrationPopup = false'
    />
    <suppress-issue-popup
      :visible-popup='suppressDialog.visible'
      :issues-count='suppressDialog.issue ? 1 : selectedCount'
      :initial='suppressDialog.initial'
      :loading='suppressionActionLoading'
      @submit='submitSuppression'
      @closed='suppressDialog.visible = false'
    />
    <yes-no-popup
      :visible-popup='restoreDialog.visible'
      title='suppression.restore.confirm.title'
      message='suppression.restore.confirm.message'
      yes-caption='suppression.action.restore'
      no-caption='suppression.dialog.cancel'
      :loading='suppressionActionLoading'
      width='420px'
      @clicked='onRestoreConfirm'
    />
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import { issuesMainLayoutColumns } from '@/services/tableColumns';
import { mapGetters } from 'vuex';
import IssueSeverityChipTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueSeverityChipTableCell.vue';
import IssueEPSSTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueEPSSTableCell.vue';
import DateTableCell from '@/components/tableCells/centralizedIssuesTableCells/DateTableCell.vue';
import TldTableCell from '@/components/tableCells/centralizedIssuesTableCells/TldTableCell.vue';
import AssetTableCell from '@/components/tableCells/centralizedIssuesTableCells/AssetTableCell.vue';
import IssueNameTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueNameTableCell.vue';
import IssueActionsTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueActionsTableCell.vue';
import IssueCycleTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueCycleTableCell.vue';
import IssuePriorityTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssuePriorityTableCell.vue';
import IssueSourceTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueSourceTableCell.vue';
import IssueStatusTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueStatusTableCell.vue';
import IssueSelectTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueSelectTableCell.vue';
import {
  SET_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW,
  CLEAR_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW,
  TOGGLE_SUPPRESS_STATUS_FOR_ISSUE,
  BULK_TOGGLE_SUPPRESS_STATUS_FOR_ISSUES,
} from '@/store/centralizedIssues/actionTypes';
import { CLEAR_ISSUE_DETAILS } from '@/store/issue/actionTypes';
import { EXECUTE_INTEGRATION } from '@/store/integration/actionTypes';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import TicketingIntegrationPopup from '@/components/popup/TicketingIntegrationPopup.vue';
import AutomaticIssueDetailsPopup from '@/components/popup/AutomaticIssueDetailsPopup.vue';
import SuppressIssuePopup from '@/components/popup/SuppressIssuePopup.vue';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import { LOAD_PASSIVE_RISK_SCORE } from '@/store/statistics/actionTypes';
import { issueStatusKey } from '@/utils/suppression';
import TechnologyIssueBody from '@/components/DetailedIssuesBody/TechnologyIssueBody.vue';
import HijackableAssetIssueBody from '@/components/DetailedIssuesBody/HijackableAssetIssueBody.vue';
import WafIssueBody from '@/components/DetailedIssuesBody/WafIssueBody.vue';
import CloudAssetIssueBody from '@/components/DetailedIssuesBody/CloudAssetIssueBody.vue';
import BucketIssueBody from '@/components/DetailedIssuesBody/BucketIssueBody.vue';
import DataLeakIssueBody from '@/components/DetailedIssuesBody/DataLeakIssueBody.vue';
import EmployeesIssueBody from '@/components/DetailedIssuesBody/EmployeesIssueBody.vue';
import PasswordSprayIssueBody from '@/components/DetailedIssuesBody/PasswordSprayIssueBody.vue';
import VulnerabilityAssessmentIssueBody from '@/components/DetailedIssuesBody/VulnerabilityAssessmentIssueBody.vue';
import AutomatedExploitationIssueBody from '@/components/DetailedIssuesBody/AutomatedExploitationIssueBody.vue';
import DefaultDetailedIssueBody from '@/components/DetailedIssuesBody/DefaultDetailedIssueBody.vue';
import SmtpIssueBody from '@/components/DetailedIssuesBody/SmtpIssueBody.vue';
import DnsIssueBody from '@/components/DetailedIssuesBody/DnsIssueBody.vue';
import PortIssueBody from '@/components/DetailedIssuesBody/PortIssueBody.vue';
import SitemapIssueBody from '@/components/DetailedIssuesBody/SitemapIssueBody.vue';

export const IssueTypeNames = {
  hijack: 'Hijacked Asset',
  cloud: 'Exposed Cloud Asset',
  bucket: 'Exposed Cloud Bucket',
  technologies: 'Vulnerable Technology Found',
  employees: 'Leak Credentials For Employee',
  dataleaks: 'Data Leak Discovered',
  vulnerability: 'Vulnerability Found on Asset',
  automatedExploitation: 'CVE Found on Host',
  waf: 'Asset Not Behind Waf',
  passwordSpray: 'Validated Leak Credential For Employee',
  smtp: 'SMTP Vulnerability Found',
  dns: 'DNS Vulnerability Found',
  port: 'Exposed Ports and Services',
  cloudDiscovery: 'Exposed Port on Cloud instance',
  sitemap: 'Missing Feature Identified For Website',
};

export default {
  name: 'CentralizedIssuesDataView',
  components: {
    PageLoadingOverlay,
    AdvancedPagination,
    WombatTable,
    WombatRowDefaultCell,
    DownloadCSV,
    TicketingIntegrationPopup,
    AutomaticIssueDetailsPopup,
    SuppressIssuePopup,
    YesNoPopup,
    ErrorMessage,
    IssueSelectTableCell,
  },
  props: {
    issuesList: {
      type: Array,
      default: () => [],
      required: true,
    },
    forProject: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      prioritySortDirection: -1,
      openTicketingIntegrationPopup: false,
      errorMessage: '',
      openDetailedIssuePopup: false,
      // Selection model mirrors the Inventory tabs:
      // - isAllSelected + excludedIssueIds represents "all matching except these".
      // - selectedIssueIds represents an explicit pick when not in all-selected mode.
      selectedIssueIds: [],
      excludedIssueIds: [],
      isAllSelected: false,
      // issue = null means the action applies to the current table selection.
      suppressDialog: { visible: false, issue: null, initial: null },
      restoreDialog: { visible: false, issue: null },
      suppressionActionLoading: false,
      detailsRefreshKey: 0,
    };
  },
  created() {
    this.openDetailedIssuePopup = this.openDetailedIssuePopupOnStart;
  },
  watch: {
    selectedIssueIds() {
      // Picking every matching row promotes to "all selected".
      if (this.totalMatching && this.selectedIssueIds.length === this.totalMatching && !this.isAllSelected) {
        this.isAllSelected = true;
      }
    },
    excludedIssueIds() {
      // Excluding every matching row means nothing is selected anymore.
      if (this.totalMatching && this.excludedIssueIds.length === this.totalMatching && this.isAllSelected) {
        this.isAllSelected = false;
      }
    },
    isAllSelected() {
      if (this.isAllSelected) {
        this.selectedIssueIds = [];
      } else {
        this.excludedIssueIds = [];
      }
    },
    filterSignature() {
      // Changing the actual filter invalidates any selection (the matching set changed).
      this.clearSelection();
    },
  },
  methods: {
    /** Suppress (issue = null for the selection) or edit / extend (initial = current suppression). */
    openSuppressDialog(issue, initial) {
      if (!issue && !this.hasSelection) {
        return;
      }
      this.suppressDialog = { visible: true, issue: issue || null, initial: initial || null };
    },
    openRestoreConfirm(issue) {
      if (!issue && !this.hasSelection) {
        return;
      }
      this.restoreDialog = { visible: true, issue: issue || null };
    },
    async submitSuppression(suppression) {
      const done = await this.applySuppressionChange(this.suppressDialog.issue, true, suppression);
      if (done) {
        this.suppressDialog.visible = false;
      }
    },
    async onRestoreConfirm(confirmed) {
      if (!confirmed) {
        this.restoreDialog.visible = false;
        return;
      }
      const done = await this.applySuppressionChange(this.restoreDialog.issue, false, null);
      if (done) {
        this.restoreDialog.visible = false;
      }
    },
    async applySuppressionChange(issue, suppressedValue, suppression) {
      this.suppressionActionLoading = true;
      try {
        if (issue) {
          await this.$store.dispatch(TOGGLE_SUPPRESS_STATUS_FOR_ISSUE, { issueId: issue.id, suppressedValue, suppression });
        } else {
          const selection = this.isAllSelected
            ? { selectAll: true, filter: this.buildSelectAllFilter(), excludeIds: this.excludedIssueIds }
            : { issueIds: this.selectedIssueIds };
          await this.$store.dispatch(BULK_TOGGLE_SUPPRESS_STATUS_FOR_ISSUES, {
            ...selection,
            suppressedValue,
            ...(suppressedValue ? { suppression } : {}),
          });
          this.clearSelection();
        }
        // The list reloads with the new status, so close the issue dialog instead of patching it.
        await this.closeDetailedIssuePopup();
        await this.$emit('loadIssues');
        this.refreshRiskScore();
        return true;
      } catch (e) {
        this.errorMessage = e.message;
        return false;
      } finally {
        this.suppressionActionLoading = false;
      }
    },
    /** The backend recalculates the score on suppress / restore; reload it for the dashboard. */
    refreshRiskScore() {
      let domain;
      if (this.forProject) {
        domain = [this.detailedProject?.testCard?.tld].filter(Boolean);
      } else {
        domain = (this.domainFilter || []).length ? this.domainFilter : this.domains;
      }
      if (domain && domain.length) {
        this.$store.dispatch(LOAD_PASSIVE_RISK_SCORE, { domain }).catch(() => {});
      }
    },
    /** Same cleanup as the dialog's own close button. */
    async closeDetailedIssuePopup() {
      if (!this.openDetailedIssuePopup) {
        return;
      }
      this.openDetailedIssuePopup = false;
      await this.$store.dispatch(CLEAR_ISSUE_DETAILS);
      await this.$store.dispatch(CLEAR_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW);
    },
    /** Keep the open issue's status in step with what the details popup just loaded. */
    onSuppressionLoaded(current) {
      const issue = this.detailedIssueForCentralizedView;
      if (!issue || !issue.id) {
        return;
      }
      const before = JSON.stringify(issue.suppression || null);
      if (before !== JSON.stringify(current || null)) {
        this.$store.dispatch(SET_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW, {
          ...issue,
          suppression: current,
          status: issueStatusKey({ suppression: current, status: issue.issueStatus || issue.status }),
        });
      }
    },
    toggleIssueSelection(issue) {
      if (this.isAllSelected) {
        // In all-selected mode, ticking/unticking a row toggles it in the exclusion list.
        const index = this.excludedIssueIds.indexOf(issue.id);
        if (index === -1) {
          this.excludedIssueIds.push(issue.id);
        } else {
          this.excludedIssueIds.splice(index, 1);
        }
        return;
      }
      const index = this.selectedIssueIds.indexOf(issue.id);
      if (index === -1) {
        this.selectedIssueIds.push(issue.id);
      } else {
        this.selectedIssueIds.splice(index, 1);
      }
    },
    selectAll() {
      // Single top checkbox: toggles "all matching" on/off, clearing any partial state.
      this.isAllSelected = !this.isAllSelected;
      this.selectedIssueIds = [];
      this.excludedIssueIds = [];
    },
    clearSelection() {
      this.isAllSelected = false;
      this.excludedIssueIds = [];
      this.selectedIssueIds = [];
    },
    buildSelectAllFilter() {
      const options = this.centralizedIssuesListOptions || {};
      const filter = {
        search: options.search,
        toggles: options.toggles,
        issueTypes: options.issueTypes || [],
      };
      if (this.forProject) {
        filter.projectId = this.detailedProject.id;
      } else {
        const selectedDomains = this.domainFilter || [];
        filter.domain = selectedDomains.length ? selectedDomains : this.domains;
      }
      return filter;
    },
    async sortByPriority() {
      this.prioritySortDirection *= -1;
      await this.columnSorted({ column: { field: 'priorityScore' }, direction: this.prioritySortDirection > 0 ? 'asc' : 'desc' });
    },
    async columnSorted({ column, direction }) {
      await this.$emit('columnSorted', { column, direction });
    },
    async integrationClicked(issue) {
      await this.$store.dispatch(SET_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW, issue);
      try {
        await this.$store.dispatch(EXECUTE_INTEGRATION, { integrationCategory: 'Ticketing', integrationAction: 'open_ticket', executionData: issue });
        this.openTicketingIntegrationPopup = true;
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async toggleDetailedIssuePopup(issue) {
      if (!this.openDetailedIssuePopup) {
        await this.$store.dispatch(SET_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW, issue);
        this.openDetailedIssuePopup = true;
      } else {
        this.openDetailedIssuePopup = false;
      }
    },
    async changedSelectedPage(currentPage) {
      await this.$emit('changedSelectedPage', currentPage);
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      await this.$emit('changedItemsAmountPerPage', itemsPerPage);
    },
    async downloadCSVReport() {
      await this.$emit('downloadCSVReport');
    },
  },
  computed: {
    ...mapGetters([
      'openDetailedIssuePopupOnStart',
      'centralizedIssuesListSort',
      'centralizedIssuesPaginationState',
      'centralizedIssues',
      'centralizedIssuesListLoading',
      'projectIssuesListLoading',
      'detailedIssueForCentralizedView',
      'paginationItemsPerPageByTabs',
      'issuesProgress',
      'centralizedIssuesListOptions',
      'domainFilter',
      'domains',
      'detailedProject',
    ]),
    loading() {
      if (this.forProject) {
        return this.projectIssuesListLoading;
      }
      return this.centralizedIssuesListLoading;
    },
    issueBodyComponentsHash() {
      return {
        [IssueTypeNames.technologies]: TechnologyIssueBody,
        [IssueTypeNames.hijack]: HijackableAssetIssueBody,
        [IssueTypeNames.waf]: WafIssueBody,
        [IssueTypeNames.cloud]: CloudAssetIssueBody,
        [IssueTypeNames.bucket]: BucketIssueBody,
        [IssueTypeNames.dataleaks]: DataLeakIssueBody,
        [IssueTypeNames.employees]: EmployeesIssueBody,
        [IssueTypeNames.passwordSpray]: PasswordSprayIssueBody,
        [IssueTypeNames.vulnerability]: VulnerabilityAssessmentIssueBody,
        [IssueTypeNames.automatedExploitation]: AutomatedExploitationIssueBody,
        [IssueTypeNames.smtp]: SmtpIssueBody,
        [IssueTypeNames.dns]: DnsIssueBody,
        [IssueTypeNames.port]: PortIssueBody,
        [IssueTypeNames.cloudDiscovery]: PortIssueBody,
        [IssueTypeNames.sitemap]: SitemapIssueBody,
        default: DefaultDetailedIssueBody,
      };
    },
    iconForPrioritySorting() {
      return this.prioritySortDirection > 0 ? 'arrow_upward' : 'arrow_downward';
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.centralizedIssues;
    },
    pages() {
      return this.centralizedIssuesPaginationState?.pageCount || Math.ceil(this.issuesList.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    // Ids considered selected among the rows currently on the page (drives the checkboxes).
    effectiveSelectedIdsOnPage() {
      if (this.isAllSelected) {
        return this.issuesList
          .filter(issue => !this.excludedIssueIds.includes(issue.id))
          .map(issue => issue.id);
      }
      return this.selectedIssueIds;
    },
    totalMatching() {
      return this.centralizedIssuesPaginationState?.total || this.issuesList.length;
    },
    selectedCount() {
      if (this.isAllSelected) {
        return Math.max(this.totalMatching - this.excludedIssueIds.length, 0);
      }
      return this.selectedIssueIds.length;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    isIndeterminate() {
      // Partial selection shows the dash; a full selection shows a solid check.
      return this.selectedCount > 0 && this.selectedCount < this.totalMatching;
    },
    filterSignature() {
      const options = this.centralizedIssuesListOptions || {};
      const scope = this.forProject
        ? this.detailedProject && this.detailedProject.id
        : this.domainFilter || [];
      return JSON.stringify({
        scope,
        search: options.search,
        toggles: options.toggles,
        issueTypes: options.issueTypes,
      });
    },
    tableColumns() {
      const columns = [
        issuesMainLayoutColumns.priority,
        issuesMainLayoutColumns.name,
        issuesMainLayoutColumns.severity,
        issuesMainLayoutColumns.exploitability,
        issuesMainLayoutColumns.asset,
        issuesMainLayoutColumns.tld,
        issuesMainLayoutColumns.source,
        issuesMainLayoutColumns.firstSeen,
        issuesMainLayoutColumns.lastSeen,
        issuesMainLayoutColumns.cycles,
        issuesMainLayoutColumns.status,
        issuesMainLayoutColumns.actions,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
          sort: this.centralizedIssuesListSort[column.sortingField || column.field],
        }));

      const selectColumn = {
        name: 'select', field: 'select', class: 'text issue-select-column', width: '72px',
      };

      return [selectColumn, ...columns];
    },
    tableRowCellComponentsHash() {
      return {
        select: IssueSelectTableCell,
        severity: IssueSeverityChipTableCell,
        exploitability: IssueEPSSTableCell,
        date: DateTableCell,
        tld: TldTableCell,
        asset: AssetTableCell,
        firstSeen: DateTableCell,
        lastSeen: DateTableCell,
        name: IssueNameTableCell,
        actions: IssueActionsTableCell,
        cycles: IssueCycleTableCell,
        priority: IssuePriorityTableCell,
        source: IssueSourceTableCell,
        status: IssueStatusTableCell,
        default: WombatRowDefaultCell,
      };
    },
    getCounter() {
      let from = ((this.centralizedIssuesPaginationState.page - 1) * this.centralizedIssuesPaginationState.count) + 1;
      let to = this.centralizedIssuesPaginationState.page * this.centralizedIssuesPaginationState.count;
      if (Number(this.centralizedIssuesPaginationState.page) === this.centralizedIssuesPaginationState.pageCount) {
        from = (this.centralizedIssuesPaginationState.total - this.centralizedIssuesPaginationState.count) + 1;
        to = this.centralizedIssuesPaginationState.total;
      }
      if (to === 0) {
        from = 0;
      }
      return `${from} to ${to} of ${this.centralizedIssuesPaginationState.total}`;
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";
.centralized-issues-data-view {
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 180px); // Ensure overlay covers full content area
  .issues-tab-loading {
    align-self: center;
    justify-self: center;
    margin-top: 20%;
  }
  .counter{
    padding-left: $space-8;
    margin-top: rem(-30);
    margin-bottom: $space-4;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    text-transform: uppercase;
    color: var(--text-secondary);

  }
  .issues-list-header {
    display: flex;
    justify-content: space-between;
    padding-left: $space-8;
    padding-top: $space-8;
    margin-bottom: $space-7;
    padding-right: $space-8;
    .download-csv-btn {
      height: $btn-height-md;
      margin-right: $space-6;
      &:hover {
        cursor: pointer;
      }
      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
    .title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-3xl;
      font-weight: bold;
      line-height: $space-5;
      letter-spacing: 0;
    }
    .actions {
      display: flex;
      flex-direction: row;
    }
    .priority-sort {
      border: solid $border-width-thin transparent;
      border-radius: $radius-sm;
      height: rem(30);
      padding: rem(2) $space-6 rem(2) rem(2);
      color: var(--text-primary);

      .v-icon {
        color: var(--text-primary) !important;
      }

      &:hover {
        cursor: pointer;
        background-color: var(--bg-tertiary);
      }
    }
  }
  .bulk-actions-bar {
    display: flex;
    align-items: center;
    padding-left: 0;
    padding-right: $space-8;
    margin-bottom: $space-4;
    min-height: rem(40);
    &__select-all {
      // Size to content but keep the same inset as the title so the checkbox aligns above the row checkboxes.
      display: flex;
      justify-content: flex-start;
      padding-left: $space-8;
      margin: 0;
    }
    &__label {
      font-family: $font-family-medium;
      font-size: $font-size-sm;
      text-transform: uppercase;
      color: var(--text-secondary);
      margin-left: $space-2;
      white-space: nowrap;
    }
    &__actions-btn {
      // Match the inventory "Add asset" button styling.
      &.bulk-actions-bar__actions-btn {
        background-color: var(--text-secondary) !important;
        border: none !important;
        border-radius: $radius-sm;
        box-shadow: $shadow-sm;
        color: var(--text-inverse) !important;
        font-family: $font-family-medium;
        font-size: $font-size-sm;
        height: $btn-height-md;
        letter-spacing: 1.1px;
        line-height: $font-size-md;
        text-transform: uppercase;
        .v-icon {
          color: var(--text-inverse) !important;
        }
        // Vuetify renders the loading spinner internally - pierce scoped styles to make it white.
        ::v-deep .v-btn__loader .v-progress-circular {
          color: var(--text-inverse) !important;
        }
      }
    }
  }
  .issues-list-content {
    &__footer{
      margin-top: $space-6;
      margin-bottom: $space-11;
    }
    &.loading {
      display: none;
    }
  }
}
</style>

<style lang="scss">
@import "~@/assets/styles/design-system";

// Bulk-actions dropdown is teleported to the app root, so it lives outside the scoped tree.
.bulk-actions-menu {
  .v-list-item {
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background-color: var(--brand-primary-bg);

      .v-list-item__title {
        color: var(--brand-primary);
      }
    }
  }
}

.centralized-issues-data-view {
  .wombat-table {
    // Lock the checkbox column so the table's flexbox can't shrink it below this width.
    .cell.issue-select-column {
      flex: 0 0 rem(72);
      min-width: rem(72);
    }
    .wombat-overlay {
      position: relative;
      border-bottom: none;

      &:hover {
        background-color: var(--bg-tertiary);
        --displayactioncell: flex;
      }

      &:hover::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 20%;
        height: 100%;
        background: linear-gradient(90deg, transparent 0%, var(--bg-tertiary) 70%);
        pointer-events: none;
        z-index: 2;
      }
    }
  }
}
</style>
